import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Mail, Users, RefreshCw, Inbox as InboxIcon } from "lucide-react";
import { Reveal } from "@/components/Motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPIC_COLORS = {
  business: "#E05A47",
  partnership: "#2D6A4F",
  general: "#38A3A5",
};

const fmt = (ts) => {
  try {
    return new Date(ts).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return ts;
  }
};

export default function Inbox() {
  const [key, setKey] = useState(sessionStorage.getItem("vp_admin_key") || "");
  const [enquiries, setEnquiries] = useState(null);
  const [waitlist, setWaitlist] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const headers = { "X-Admin-Key": key };
      const [eRes, wRes] = await Promise.all([
        axios.get(`${API}/admin/enquiries`, { headers }),
        axios.get(`${API}/admin/waitlist`, { headers }),
      ]);
      sessionStorage.setItem("vp_admin_key", key);
      setEnquiries(eRes.data);
      setWaitlist(wRes.data);
      toast.success("Inbox loaded");
    } catch (err) {
      if (err.response?.status === 401) toast.error("Invalid admin key");
      else toast.error("Couldn't load the inbox");
    } finally {
      setLoading(false);
    }
  };

  const loaded = enquiries !== null && waitlist !== null;

  return (
    <main data-testid="page-inbox" className="min-h-screen bg-[#F4F7F4] pt-32 pb-24">
      <div className="mx-auto max-w-6xl px-6 sm:px-10 lg:px-16">
        <Reveal>
          <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-[#2D6A4F]">
            Private · VeloPaths team only
          </p>
          <h1 className="mt-3 font-display text-3xl sm:text-4xl font-black tracking-tight text-[#0B2B22]">
            Enquiry Inbox
          </h1>
          <p className="mt-3 max-w-xl text-sm sm:text-base text-[#52635B]">
            Every stored contact-form enquiry and launch-waitlist signup, in one place.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            data-testid="inbox-key-form"
            onSubmit={load}
            className="mt-8 flex max-w-lg flex-col gap-3 rounded-2xl border border-[#154234]/10 bg-white p-5 shadow-lg sm:flex-row sm:items-center"
          >
            <div className="relative flex-1">
              <Lock size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#52635B]" />
              <input
                data-testid="input-admin-key"
                type="password"
                required
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="Admin key"
                className="w-full rounded-xl border border-[#154234]/20 bg-white py-3 pl-10 pr-4 text-sm text-[#0B2B22] placeholder:text-[#52635B]/50 focus:outline-none focus:ring-2 focus:ring-[#2D6A4F]/40"
              />
            </div>
            <button
              type="submit"
              data-testid="btn-load-inbox"
              disabled={loading || !key}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#0B2B22] px-6 py-3 text-sm font-bold text-[#F4F7F4] transition-colors hover:bg-[#154234] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? <RefreshCw size={15} className="animate-spin" /> : <InboxIcon size={15} />}
              {loading ? "Loading…" : loaded ? "Refresh" : "Load inbox"}
            </button>
          </form>
        </Reveal>

        {loaded && (
          <div className="mt-12 grid gap-10 lg:grid-cols-2">
            <section>
              <div className="flex items-center gap-3">
                <span className="grid size-10 place-items-center rounded-xl bg-[#0B2B22] text-[#7FB998]">
                  <Mail size={17} />
                </span>
                <h2 className="font-display text-xl font-bold tracking-tight text-[#0B2B22]">
                  Enquiries{" "}
                  <span className="font-mono text-sm font-bold text-[#E05A47]">
                    ({enquiries.length})
                  </span>
                </h2>
              </div>
              <div data-testid="inbox-enquiries-list" className="mt-5 space-y-4">
                {enquiries.length === 0 && (
                  <p className="rounded-2xl border border-dashed border-[#154234]/20 p-6 text-sm text-[#52635B]">
                    No enquiries yet.
                  </p>
                )}
                {enquiries.map((q, i) => (
                  <div
                    key={q.id || i}
                    data-testid={`inbox-enquiry-${i}`}
                    className="rounded-2xl border border-[#154234]/10 bg-white p-5"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className="rounded-full px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-white"
                        style={{ backgroundColor: TOPIC_COLORS[q.topic] || "#52635B" }}
                      >
                        {q.topic}
                      </span>
                      <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#52635B]">
                        {fmt(q.timestamp)}
                      </span>
                    </div>
                    <p className="mt-3 font-display text-base font-bold text-[#0B2B22]">
                      {q.name}
                      {q.organization ? (
                        <span className="font-normal text-[#52635B]"> · {q.organization}</span>
                      ) : null}
                    </p>
                    <a
                      href={`mailto:${q.email}`}
                      className="mt-0.5 block text-sm font-semibold text-[#2D6A4F] hover:underline"
                    >
                      {q.email}
                    </a>
                    <p className="mt-3 text-sm leading-relaxed text-[#52635B]">{q.message}</p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <div className="flex items-center gap-3">
                <span className="grid size-10 place-items-center rounded-xl bg-[#0B2B22] text-[#7FB998]">
                  <Users size={17} />
                </span>
                <h2 className="font-display text-xl font-bold tracking-tight text-[#0B2B22]">
                  Waitlist{" "}
                  <span className="font-mono text-sm font-bold text-[#E05A47]">
                    ({waitlist.length})
                  </span>
                </h2>
              </div>
              <div data-testid="inbox-waitlist-list" className="mt-5 space-y-3">
                {waitlist.length === 0 && (
                  <p className="rounded-2xl border border-dashed border-[#154234]/20 p-6 text-sm text-[#52635B]">
                    No waitlist signups yet.
                  </p>
                )}
                {waitlist.map((w, i) => (
                  <div
                    key={w.id || i}
                    data-testid={`inbox-waitlist-${i}`}
                    className="flex items-center justify-between gap-4 rounded-2xl border border-[#154234]/10 bg-white px-5 py-4"
                  >
                    <a
                      href={`mailto:${w.email}`}
                      className="text-sm font-semibold text-[#0B2B22] hover:underline"
                    >
                      {w.email}
                    </a>
                    <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.15em] text-[#52635B]">
                      {fmt(w.timestamp)}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
      </div>
    </main>
  );
}
